// LogisticManagerDashboard.js
import React, { useState, useEffect, useMemo } from 'react';
import styles from '../../styles/Dashboard/LogisticManagerDashboard.module.css';
import { auth, db } from "../../firebase";
import { useNavigate } from "react-router-dom";
import {
  collection,
  getDocs,
  updateDoc,
  doc,
  query,
  where,
  getDoc,
  orderBy
} from 'firebase/firestore';
import { FaSignOutAlt } from 'react-icons/fa';
import { format } from 'date-fns';
import * as XLSX from 'xlsx';
import logo from "../../assets/logo.jpg"; // adjust path as needed
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';


const ORDERS_PER_PAGE = 10;

// 🔹 Normalizes an RSM name so minor data-entry differences (extra spaces,
// different capitalization) don't create duplicate/broken groups or cause
// exact-match filters to silently miss real records.
const normalizeRsmName = (name) => (name || "N/A").toString().trim().replace(/\s+/g, " ") || "N/A";
const normalizeRsmKey = (name) => normalizeRsmName(name).toLowerCase();

// 🔹 Orders-table columns the user can hide/unhide (Select + Actions stay visible
// so exports and approve/reject are never lost by accident).
const TOGGLEABLE_COLUMNS = [
  { key: "date", label: "Date" },
  { key: "placedBy", label: "Order Placed By" },
  { key: "rsm", label: "BM/RSM" },
  { key: "partyCode", label: "Party Code" },
  { key: "partyName", label: "Party Name" },
  { key: "partyNumber", label: "Party Number" },
  { key: "pod", label: "POD" },
  { key: "contactInfo", label: "Contact Info" },
  { key: "products", label: "Products" },
  { key: "commitment", label: "Commitment" },
  { key: "ledger", label: "Ledger" },
  { key: "status", label: "Status" },
  { key: "finalApproval", label: "Final Approval By" },
];
const HIDDEN_COLUMNS_STORAGE_KEY = "lmDashboardHiddenColumns";

const LogisticManagerDashboard = () => {
  const [userName, setUserName] = useState("");
  const [orders, setOrders] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [activeStatus, setActiveStatus] = useState("Pending");
  const [mainView, setMainView] = useState("home"); // "home" | "rsmHome" | "table"
  const [selectedRsm, setSelectedRsm] = useState(null);
  const [soFilter, setSoFilter] = useState("All");
  const [rsmFilter, setRsmFilter] = useState("All");
  const [partyFilter, setPartyFilter] = useState("All");
  const [productFilter, setProductFilter] = useState("All");
  const [dateRange, setDateRange] = useState({ from: "", to: "" });
  const [commitmentDateFilter, setCommitmentDateFilter] = useState("");
  const [selectedOrderIds, setSelectedOrderIds] = useState([]);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectionMessage, setRejectionMessage] = useState('');
  const [selectedRejectOrderId, setSelectedRejectOrderId] = useState(null);
  const [viewRejectMessage, setViewRejectMessage] = useState(''); // new
  const [viewRejectModalOpen, setViewRejectModalOpen] = useState(false); // new
  const [isRejecting, setIsRejecting] = useState(false);

  // Column visibility (remembered between visits)
  const [hiddenColumns, setHiddenColumns] = useState(() => {
    try {
      const saved = localStorage.getItem(HIDDEN_COLUMNS_STORAGE_KEY);
      const parsed = saved ? JSON.parse(saved) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  });
  const [showColumnPicker, setShowColumnPicker] = useState(false);

  // Product names from the catalog, used for the ledger "Product" dropdown
  const [catalogProductNames, setCatalogProductNames] = useState([]);

  useEffect(() => {
    try {
      localStorage.setItem(HIDDEN_COLUMNS_STORAGE_KEY, JSON.stringify(hiddenColumns));
    } catch (e) {
      // storage unavailable — visibility just won't persist
    }
  }, [hiddenColumns]);

  useEffect(() => {
    const fetchCatalogProducts = async () => {
      try {
        const snapshot = await getDocs(collection(db, "products"));
        const names = [
          ...new Set(
            snapshot.docs
              .map((d) => (d.data().name || "").trim())
              .filter((name) => name && name.toLowerCase() !== "n/a")
          ),
        ].sort((a, b) => a.localeCompare(b));
        setCatalogProductNames(names);
      } catch (error) {
        console.error("Error fetching catalog products:", error);
      }
    };
    fetchCatalogProducts();
  }, []);

  const isColVisible = (key) => !hiddenColumns.includes(key);

  const toggleColumn = (key) => {
    setHiddenColumns((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const showAllColumns = () => setHiddenColumns([]);


  const navigate = useNavigate();

  const fetchUserName = async () => {
    if (auth.currentUser) {
      const userRef = doc(db, "users", auth.currentUser.uid);
      const userSnap = await getDoc(userRef);
      if (userSnap.exists()) {
        const userData = userSnap.data();
        setUserName(userData.name || "Boss");
      }
    }
  };


  const fetchOrders = async () => {
    setIsLoading(true);
    try {
      const q = query(
        collection(db, "orders"),
        where("status", "in", ["BM/RSM Submitted", "Placed", "Logistic Reviewed", "Approved", "Rejected", "Rejected By Logistic"]),
        orderBy("createdAt", "desc")
      );
      const snapshot = await getDocs(q);

      const data = await Promise.all(
        snapshot.docs.map(async (docSnap) => {
          const orderData = docSnap.data();
          let soName = "N/A";
          let rsmName = orderData.rsmName || "N/A";
          let finalApprovedByName = "N/A";

          // 🔹 Fetch SO Name
          if (orderData.soId) {
            try {
              const soDoc = await getDocs(
                query(collection(db, "users"), where("__name__", "==", orderData.soId))
              );
              soDoc.forEach((doc) => {
                const u = doc.data();
                soName = u.name || u.email || "S.O.";
              });
            } catch (err) {
              console.error("Failed to fetch SO:", err);
            }
          }

          // 🔹 Fetch Final Approval Name
          if (orderData.finalApprovedBy) {
            try {
              const userDoc = await getDoc(doc(db, "users", orderData.finalApprovedBy));
              if (userDoc.exists()) {
                finalApprovedByName = userDoc.data().name || userDoc.data().email || "Boss";
              }
            } catch (err) {
              console.error("Failed to fetch final approver:", err);
            }
          }

          const balance = (orderData.products || []).reduce((acc, product) => {
            const credit = parseFloat(product.credit || 0);
            const debit = parseFloat(product.debit || 0);
            return acc + (credit - debit);
          }, 0);

          return {
            id: docSnap.id,
            ...orderData,
            soName,
            rsmName,
            balance,
            finalApprovedByName, // 👈 Add it here
            partyCode: orderData.partyCode || '-',
          };
        })
      );

      // ✅ Filter orders by user email
      const currentUser = auth.currentUser;
      let filteredData = data;

      if (currentUser?.email === "lmvegetable@rifaah.org") {
        filteredData = data.filter(order =>
          order.products?.some(p => {
            const category = (p.category || "").toLowerCase();
            const name = (p.name || "").toLowerCase();
            const quantity = parseFloat(p.quantity || 0);

            // Only include real products
            const isLedgerEntry = name === "n/a" || quantity === 0;

            return !isLedgerEntry && (category === "vegetables" || category === "pearl millet" || category === "hyrbid mustard" || category === "mustard");
          })
        );
      } else if (currentUser?.email === "lmmajorcrop@rifaah.org") {
        filteredData = data.filter(order =>
          order.products?.every(p => {
            const category = (p.category || "").toLowerCase();
            const name = (p.name || "").toLowerCase();
            const quantity = parseFloat(p.quantity || 0);

            // Only consider real products
            const isLedgerEntry = name === "n/a" || quantity === 0;

            return isLedgerEntry || (category !== "vegetables" && category !== "pearl millet" && category !== "hyrbid mustard" && category !== "mustard");
          })
        );
      }



      setOrders(filteredData);
    } catch (error) {
      console.error("Error fetching orders:", error);
    }
    setIsLoading(false);
  };


  useEffect(() => {
    fetchOrders();
    fetchUserName();
  }, []);

  useEffect(() => {
    let filtered = orders;

    if (activeStatus !== "All") {
      if (activeStatus === "Pending") {
        filtered = filtered.filter(
          (order) => order.status === "BM/RSM Submitted" || order.status === "Placed" || order.status === "Pending"
        );
      } else if (activeStatus === "Dispatched") {
        filtered = filtered.filter(
          (order) => order.status === "Logistic Reviewed" || order.status === "Approved"
        );
      } else if (activeStatus === "Rejected") {
        filtered = filtered.filter(
          (order) =>
            order.status === "Rejected" ||
            order.status === "Rejected By Logistic" ||
            order.status === "Rejected By BM/RSM"
        );
      } else {
        filtered = filtered.filter((order) => order.status === activeStatus);
      }
    }

    if (soFilter !== "All") {
      filtered = filtered.filter((order) => order.soName === soFilter);
    }

    if (rsmFilter !== "All") {
      filtered = filtered.filter((order) => normalizeRsmKey(order.rsmName) === normalizeRsmKey(rsmFilter));
    }

    if (partyFilter !== "All") {
      filtered = filtered.filter((order) => order.partyName === partyFilter);
    }

    if (productFilter !== "All") {
      filtered = filtered.filter((order) =>
        (order.products || []).some((p) => (p.name || "").trim() === productFilter)
      );
    }

    if (dateRange.from) {
      const fromDate = new Date(dateRange.from);
      filtered = filtered.filter(
        (order) => order.createdAt?.toDate && order.createdAt.toDate() >= fromDate
      );
    }

    if (dateRange.to) {
      const toDate = new Date(dateRange.to);
      toDate.setHours(23, 59, 59, 999);
      filtered = filtered.filter(
        (order) => order.createdAt?.toDate && order.createdAt.toDate() <= toDate
      );
    }

    if (commitmentDateFilter) {
      const targetDate = new Date(commitmentDateFilter);
      targetDate.setHours(0, 0, 0, 0);
      filtered = filtered.filter((order) => {
        const cDate = order.commitmentDate?.toDate?.();
        return (
          cDate &&
          cDate.getFullYear() === targetDate.getFullYear() &&
          cDate.getMonth() === targetDate.getMonth() &&
          cDate.getDate() === targetDate.getDate()
        );
      });
    }

    setFilteredOrders(filtered);
    setCurrentPage(1);
  }, [orders, activeStatus, soFilter, rsmFilter, partyFilter, productFilter, dateRange, commitmentDateFilter]);

  // 🔹 Group orders by BM/RSM (respects status/SO/party/date filters, ignores rsmFilter itself)
  // so the card view always reflects an accurate per-RSM count.
  const rsmGroups = useMemo(() => {
    let base = orders;

    if (activeStatus !== "All") {
      if (activeStatus === "Pending") {
        base = base.filter(
          (order) => order.status === "BM/RSM Submitted" || order.status === "Placed" || order.status === "Pending"
        );
      } else if (activeStatus === "Dispatched") {
        base = base.filter(
          (order) => order.status === "Logistic Reviewed" || order.status === "Approved"
        );
      } else if (activeStatus === "Rejected") {
        base = base.filter(
          (order) =>
            order.status === "Rejected" ||
            order.status === "Rejected By Logistic" ||
            order.status === "Rejected By BM/RSM"
        );
      } else {
        base = base.filter((order) => order.status === activeStatus);
      }
    }

    if (soFilter !== "All") {
      base = base.filter((order) => order.soName === soFilter);
    }

    if (partyFilter !== "All") {
      base = base.filter((order) => order.partyName === partyFilter);
    }

    if (productFilter !== "All") {
      base = base.filter((order) =>
        (order.products || []).some((p) => (p.name || "").trim() === productFilter)
      );
    }

    if (dateRange.from) {
      const fromDate = new Date(dateRange.from);
      base = base.filter((order) => order.createdAt?.toDate && order.createdAt.toDate() >= fromDate);
    }

    if (dateRange.to) {
      const toDate = new Date(dateRange.to);
      toDate.setHours(23, 59, 59, 999);
      base = base.filter((order) => order.createdAt?.toDate && order.createdAt.toDate() <= toDate);
    }

    if (commitmentDateFilter) {
      const targetDate = new Date(commitmentDateFilter);
      targetDate.setHours(0, 0, 0, 0);
      base = base.filter((order) => {
        const cDate = order.commitmentDate?.toDate?.();
        return (
          cDate &&
          cDate.getFullYear() === targetDate.getFullYear() &&
          cDate.getMonth() === targetDate.getMonth() &&
          cDate.getDate() === targetDate.getDate()
        );
      });
    }

    const groups = {};
    base.forEach((order) => {
      const displayName = normalizeRsmName(order.rsmName);
      const key = normalizeRsmKey(order.rsmName);
      if (!groups[key]) {
        groups[key] = { rsmName: displayName, count: 0, pendingCount: 0, latestDate: null };
      }
      groups[key].count += 1;
      if (order.status === "BM/RSM Submitted" || order.status === "Placed") {
        groups[key].pendingCount += 1;
      }
      const created = order.createdAt?.toDate ? order.createdAt.toDate() : null;
      if (created && (!groups[key].latestDate || created > groups[key].latestDate)) {
        groups[key].latestDate = created;
      }
    });

    return Object.values(groups).sort((a, b) => a.rsmName.localeCompare(b.rsmName));
  }, [orders, activeStatus, soFilter, partyFilter, productFilter, dateRange, commitmentDateFilter]);

  // Sum of all card counts — should always equal the number of orders that
  // matched the current status/SO/party/date filters. Handy for spotting
  // any future data mismatch at a glance.
  const rsmGroupsTotal = useMemo(
    () => rsmGroups.reduce((sum, g) => sum + g.count, 0),
    [rsmGroups]
  );

  // 🔹 Top-level (home screen) RSM grid: one card per RSM with a mini
  // Pending / Dispatched / Rejected breakdown. Filtered by T.M/Party/Product/
  // date (and, if the person narrows the "All BM/RSMs" dropdown on the home
  // screen itself, by that too) — but NOT by status, since all three counts
  // are shown together on each card.
  const homeRsmGroups = useMemo(() => {
    let base = orders;

    if (soFilter !== "All") {
      base = base.filter((order) => order.soName === soFilter);
    }
    if (partyFilter !== "All") {
      base = base.filter((order) => order.partyName === partyFilter);
    }
    if (productFilter !== "All") {
      base = base.filter((order) =>
        (order.products || []).some((p) => (p.name || "").trim() === productFilter)
      );
    }
    if (dateRange.from) {
      const fromDate = new Date(dateRange.from);
      base = base.filter((order) => order.createdAt?.toDate && order.createdAt.toDate() >= fromDate);
    }
    if (dateRange.to) {
      const toDate = new Date(dateRange.to);
      toDate.setHours(23, 59, 59, 999);
      base = base.filter((order) => order.createdAt?.toDate && order.createdAt.toDate() <= toDate);
    }
    if (commitmentDateFilter) {
      const targetDate = new Date(commitmentDateFilter);
      targetDate.setHours(0, 0, 0, 0);
      base = base.filter((order) => {
        const cDate = order.commitmentDate?.toDate?.();
        return (
          cDate &&
          cDate.getFullYear() === targetDate.getFullYear() &&
          cDate.getMonth() === targetDate.getMonth() &&
          cDate.getDate() === targetDate.getDate()
        );
      });
    }
    if (rsmFilter !== "All") {
      base = base.filter((order) => normalizeRsmKey(order.rsmName) === normalizeRsmKey(rsmFilter));
    }

    const groups = {};
    base.forEach((order) => {
      const displayName = normalizeRsmName(order.rsmName);
      const key = normalizeRsmKey(order.rsmName);
      if (!groups[key]) {
        groups[key] = { rsmName: displayName, pendingCount: 0, dispatchedCount: 0, rejectedCount: 0, totalCount: 0 };
      }
      groups[key].totalCount += 1;
      if (order.status === "BM/RSM Submitted" || order.status === "Placed" || order.status === "Pending") {
        groups[key].pendingCount += 1;
      } else if (order.status === "Logistic Reviewed" || order.status === "Approved") {
        groups[key].dispatchedCount += 1;
      } else if (
        order.status === "Rejected" ||
        order.status === "Rejected By Logistic" ||
        order.status === "Rejected By BM/RSM"
      ) {
        groups[key].rejectedCount += 1;
      }
    });

    return Object.values(groups).sort((a, b) => a.rsmName.localeCompare(b.rsmName));
  }, [orders, soFilter, partyFilter, productFilter, rsmFilter, dateRange, commitmentDateFilter]);


  const handleExportPDF = async () => {
    const selectedOrders = orders.filter(order => selectedOrderIds.includes(order.id));
    const doc = new jsPDF();

    // Load company logo (PNG for transparency)
    const logoData = await toDataURL(logo);
    const watermarkData = await toDataURLWithOpacity(logo, 0.25);

    selectedOrders.forEach((order, index) => {
      if (index > 0) doc.addPage();

      const pageWidth = doc.internal.pageSize.getWidth();
      const centerX = pageWidth / 2;

      // 💧 Watermark (Centered dynamically)
      const productRows = order.products || [];
      const estimatedTableHeight = productRows.length * 8;
      const contentStartY = 50;
      const contentHeight = 100 + estimatedTableHeight;
      const contentCenterY = contentStartY + contentHeight / 2;
      doc.addImage(watermarkData, "PNG", centerX - 50, contentCenterY - 50, 100, 100);

      // 🖼️ Logo + Heading
      const logoSize = 18;
      doc.addImage(logoData, "PNG", centerX - 65, 10, logoSize, logoSize);

      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(8, 83, 65); // teal color
      doc.text("RIFAAH CROP SCIENCES PVT LTD", centerX + 5, 22, { align: "center" });
      doc.setTextColor(0, 0, 0); // reset to black for rest of text
      doc.setFontSize(11);
      doc.setFont("helvetica", "normal");

      let y = 38;

      const drawField = (label, value) => {
        const labelWidth = 45;
        const valueWidth = 130;
        const wrapped = doc.splitTextToSize(value || "N/A", valueWidth);
        doc.setFont("helvetica", "bold");
        doc.text(`${label}:`, 14, y);
        doc.setFont("helvetica", "normal");
        doc.text(wrapped, 14 + labelWidth, y);
        y += wrapped.length * 6 + 2;
      };

      // 🔷 Order-level fields
      drawField("Date", order.createdAt?.toDate?.()?.toLocaleDateString() || "N/A");
      drawField("Order By", order.soName || "N/A");
      drawField("BM / RSM", order.rsmName || "N/A");
      drawField("Party Code", order.partyCode || "N/A");
      drawField("Party Name", order.partyName || "N/A");
      drawField("Party Mobile", order.partyMobile || "N/A");
      drawField("POD", order.pod || "N/A");
      drawField("Contact Info", order.contactInfo || "N/A");

      const commitmentText = `${order.commitmentOfPayment || order.commitmentMessage || "N/A"} (${order.commitmentDate?.toDate?.()?.toLocaleDateString() || "N/A"})`;
      const commitmentDate =
        order.commitmentDate?.toDate?.()?.toLocaleDateString() ||
        order.commitmentDate ||
        "N/A";
      const status = order.status || "N/A";
      const finalApprovalBy = order.finalApprovedByName || "N/A";
      // const balanceText = order.balance > 0
      //   ? `Rs. ${order.balance} (Credit)`
      //   : order.balance < 0
      //     ? `Rs. ${Math.abs(order.balance)} (Debit)`
      //     : "Rs. 0";

      const products = order.products || [];

      const totalQty = products.reduce((sum, p) => sum + (parseFloat(p.quantity) || 0), 0);
      // 🧾 Product Table
      const body = products.map((product, i) => {
        const row = [
          // doc.splitTextToSize(product.season || "N/A", 20),
          doc.splitTextToSize(product.category || "N/A", 25),
          doc.splitTextToSize(product.name || "N/A", 25),
          doc.splitTextToSize(product.variety || "N/A", 20),
          doc.splitTextToSize(product.quantity?.toString() || "0", 10),
        ];

        if (i === 0) {
          row.push(
            // { content: balanceText, rowSpan: products.length, styles: { halign: 'center' } },
            { content: commitmentText, rowSpan: products.length, styles: { halign: 'center', fontStyle: 'italic' } },
            { content: commitmentDate, rowSpan: products.length, styles: { halign: 'center' } },
            { content: status, rowSpan: products.length, styles: { halign: 'center' } },
            { content: finalApprovalBy, rowSpan: products.length, styles: { halign: 'center' } }
          );
        }
        return row;
      });
      // ➕ Add Total Row at the bottom
      body.push([
        { content: "Total Products", colSpan: 3, styles: { halign: "center", fontStyle: "bold" } },
        { content: totalQty.toString(), styles: { halign: "center", fontStyle: "bold" } },
        { content: "", styles: { halign: "center" } },
        { content: "", styles: { halign: "center" } },
        { content: "", styles: { halign: "center" } },
        { content: "", styles: { halign: "center" } },
        { content: "", styles: { halign: "center" } },
      ]);

      autoTable(doc, {
        startY: y + 5,
        head: [["Category", "Product", "Variety", "Qty", "Commitment", "Commitment Date", "Status", "Final Approval By"]],
        body,
        theme: "grid",
        styles: { fontSize: 8, cellPadding: 1, overflow: 'linebreak', valign: 'middle' },
        headStyles: { fillColor: [8, 83, 65], textColor: 255, halign: 'center' },
        bodyStyles: { lineWidth: 0.3, lineColor: [0, 0, 0] },
        columnStyles: {
          0: { cellWidth: 18, halign: "center" },
          1: { cellWidth: 18, halign: "center" },
          2: { cellWidth: 28, halign: "center" },
          3: { cellWidth: 7, halign: "center" },
          4: { cellWidth: 28, halign: "center" },
          5: { cellWidth: 22, halign: "center" },
          6: { cellWidth: 16, halign: "center" },
          7: { cellWidth: 25, halign: "center" }
        }
      });

      // ✅ Optional Ledger Section
      if (order.ledger && order.ledger.length > 0) {
        const ledgerY = doc.lastAutoTable.finalY + 10;
        doc.setFontSize(12);
        doc.setFont("helvetica", "bold");
        doc.text("Ledger", 14, ledgerY - 4);

        let totalCredit = 0;
        let totalDebit = 0;
        order.ledger.forEach(l => {
          totalCredit += parseFloat(l.credit) || 0;
          totalDebit += parseFloat(l.debit) || 0;
        });

        const totalBalance = totalCredit - totalDebit;
        const totalBalanceText =
          totalBalance >= 0
            ? `${totalBalance.toLocaleString()} Cr`
            : `${Math.abs(totalBalance).toLocaleString()} Dr`;

        const ledgerData = order.ledger.map((l, idx) => [
          l.season || "N/A",
          l.productName || "N/A",
          l.debit ? l.debit.toLocaleString() : "0",
          l.credit ? l.credit.toLocaleString() : "0",
          idx === 0 ? totalBalanceText : "",
        ]);

        autoTable(doc, {
          startY: ledgerY,
          head: [["Season", "Product", "Debit (Rs.)", "Credit (Rs.)"]],
          body: ledgerData,
          theme: "grid",
          styles: { fontSize: 8, cellPadding: 1.5, valign: "middle" },
          headStyles: { fillColor: [8, 83, 65], textColor: 255, halign: "center" },
          bodyStyles: { lineWidth: 0.3, lineColor: [0, 0, 0] },
          columnStyles: {
            0: { cellWidth: 25, halign: "center" },
            1: { cellWidth: 55, halign: "center" },
            2: { cellWidth: 25, halign: "center" },
            3: { cellWidth: 25, halign: "center" },
          },
        });

        const balanceY = doc.lastAutoTable.finalY + 6;
        doc.setFontSize(10);
        doc.setFont("helvetica", "bold");
        doc.text(`Closing Balance: ${totalBalanceText}`, 14, balanceY);
      }
    });

    doc.save("Order_List.pdf");
    toast.success("PDF Exported Successfully!");
  };

  // 🧩 Helper functions
  function toDataURLWithOpacity(url, opacity = 0.55) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "Anonymous";
      img.src = url;
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext("2d");
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.globalAlpha = opacity;
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL("image/png"));
      };
      img.onerror = reject;
    });
  }

  function toDataURL(url) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "Anonymous";
      img.src = url;
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL("image/png"));
      };
      img.onerror = reject;
    });
  }

  const handleExportSelected = () => {
    const selectedOrders = orders.filter(order => selectedOrderIds.includes(order.id));

    const data = selectedOrders.map(order => {
      const products = order.products || [];
      const ledger = order.ledger || [];

      const productList = products
        .map(p => `${p.variety || ''} (${p.quantity ?? 0})`)
        .join("\n");

      const categoryList = [...new Set(products.map(p => p.category || ""))].join("\n");
      const totalQuantity = products.reduce((sum, p) => sum + (Number(p.quantity) || 0), 0);

      let totalCredit = 0;
      let totalDebit = 0;
      ledger.forEach(l => {
        totalCredit += parseFloat(l.credit) || 0;
        totalDebit += parseFloat(l.debit) || 0;
      });

      const totalBalance = totalCredit - totalDebit;
      const totalBalanceText =
        totalBalance >= 0
          ? `${totalBalance.toLocaleString()} Cr`
          : `${Math.abs(totalBalance).toLocaleString()} Dr`;

      const commitmentDate =
        order.commitmentDate?.toDate?.()?.toLocaleDateString("en-GB") ||
        order.commitmentDate ||
        "";

      return {
        "Date": order.createdAt?.toDate?.().toLocaleDateString("en-GB") || "",
        "SO": order.soName || "",
        "RSM": order.rsmName || "",
        "Party": `${order.partyCode || ""} - ${order.partyName || ""}`,
        "Mobile": order.partyMobile || "",
        "POD": order.pod || "",
        "Category": categoryList,
        "Products (Qty)": productList,
        "Total Qty": totalQuantity,
        "Closing Balance": totalBalanceText,
        "Commitment": order.commitmentOfPayment || order.commitmentMessage || "",
        "Commitment Date": commitmentDate, // ✅ Ensure this shows up properly
        "Status": order.status || "",
        "Approved By": order.finalApprovedByName || "",
        "Signature": "",
      };
    });

    const companyName = "RIFAAH CROP SCIENCES PVT. LTD";

    const worksheet = XLSX.utils.json_to_sheet([]);

    XLSX.utils.sheet_add_aoa(worksheet, [[companyName]], { origin: "A1" });
    // XLSX.utils.sheet_add_aoa(worksheet, [[]], { origin: "A2" });
    XLSX.utils.sheet_add_json(worksheet, data, { origin: "A2", skipHeader: false });

    const numCols = Object.keys(data[0] || {}).length;
    worksheet["!merges"] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: numCols - 1 } }];

    // ✅ Adjusted column widths to include Commitment Date column
    worksheet["!cols"] = [
      { wch: 10 },  // Date
      { wch: 8 },   // SO
      { wch: 10 },  // RSM
      { wch: 22 },  // Party
      { wch: 12 },  // Mobile
      { wch: 12 },  // POD
      { wch: 16 },  // Category
      { wch: 45 },  // Products (Qty)
      { wch: 8 },   // Total Qty
      { wch: 12 },  // Closing Balance
      { wch: 20 },  // Commitment
      { wch: 14 },  // Commitment Date ✅ Added this
      { wch: 10 },  // Status
      { wch: 14 },  // Approved By
      { wch: 12 },  // Signature
    ];

    // 🧠 Enable text wrapping for multi-line columns
    const range = XLSX.utils.decode_range(worksheet["!ref"]);
    for (let R = 2; R <= range.e.r; ++R) {
      [6, 7].forEach(C => {
        const cellRef = XLSX.utils.encode_cell({ r: R, c: C });
        const cell = worksheet[cellRef];
        if (cell) {
          if (!cell.s) cell.s = {};
          cell.s.alignment = { wrapText: true, vertical: "top" };
        }
      });
    }

    const workbook = XLSX.utils.book_new();
    const sanitizeSheetName = name =>
      name.replace(/[\\/?*:[\]]/g, '').substring(0, 31) || 'Orders';

    XLSX.utils.book_append_sheet(workbook, worksheet, sanitizeSheetName("Order List"));
    XLSX.writeFile(workbook, "Orders_List.xlsx");

    toast.success("Excel File Exported Successfully");
  };



  const handleProductChange = (orderId, index, field, value) => {
    setOrders(prev =>
      prev.map(order =>
        order.id === orderId
          ? {
            ...order,
            products: order.products.map((product, i) =>
              i === index ? { ...product, [field]: value } : product
            ),
          }
          : order
      )
    );
  };

  const handleAddProduct = (orderId) => {
    setOrders(prev =>
      prev.map(order =>
        order.id === orderId
          ? {
            ...order,
            products: [
              ...order.products,
              {
                season: '',
                category: '',
                name: 'N/A',
                variety: 'N/A',
                packSize: '',
                packType: '',
                quantity: 0,
                debit: '',
                credit: '',
                addedBy: 'LM' // ✅ Tag this product
              }
            ]
          }
          : order
      )
    );
    toast.success("New Product Added Successfully");
  };

  const handleRemoveProduct = (orderId, indexToRemove) => {
    setOrders(prev =>
      prev.map(order =>
        order.id === orderId
          ? {
            ...order,
            products: order.products.filter((_, i) => i !== indexToRemove),
          }
          : order
      )
    );
    toast.info("Product Removed Successfully");
  };

  const handleApprove = async (order) => {
    try {
      const orderRef = doc(db, 'orders', order.id);

      // Ensure credit/debit are stored as numbers
      const updatedProducts = order.products.map(p => ({
        ...p,
        credit: parseFloat(p.credit || 0),
        debit: parseFloat(p.debit || 0),
      }));

      // Recalculate total balance
      const totalBalance = updatedProducts.reduce((acc, p) => acc + (p.credit - p.debit), 0);

      await updateDoc(orderRef, {
        status: 'Logistic Reviewed',
        rejectionMessage: rejectionMessage.trim(),
        finalApprovedBy: '', // Clear previous approver
        products: updatedProducts, // ✅ Save updated product credit/debit
        balance: totalBalance      // ✅ Save balance (optional if used elsewhere)
      });

      toast.success("Order Approved Successfully");
      await fetchOrders();
    } catch (err) {
      console.error(err);
      toast.error("Failed To Approve Order");
    }
  };


  // --- Commitment Handlers ---
  // Editable only while status is "BM/RSM Submitted" or "Placed"; read-only afterward.
  const handleCommitmentChange = (orderId, field, value) => {
    setOrders((prevOrders) =>
      prevOrders.map((order) =>
        order.id === orderId ? { ...order, [field]: value } : order
      )
    );
  };

  const saveCommitment = async (orderId) => {
    try {
      const orderToSave = orders.find((order) => order.id === orderId);
      if (!orderToSave) return;

      await updateDoc(doc(db, "orders", orderId), {
        commitmentOfPayment: orderToSave.commitmentOfPayment || "",
        commitmentDate: orderToSave.commitmentDate || "",
      });

      toast.success("Commitment updated successfully!");
    } catch (error) {
      console.error("Error saving commitment:", error);
      toast.error("Failed to update commitment.");
    }
  };

  // --- Ledger Handlers ---

  // Handle changes in ledger fields (edit existing or newly added rows)
  const handleLedgerChange = (orderId, index, field, value) => {
    setOrders((prevOrders) =>
      prevOrders.map((order) => {
        if (order.id === orderId) {
          const updatedLedger = (order.ledger || []).map((entry, i) =>
            i === index ? { ...entry, [field]: value } : entry
          );

          // 🔹 Recalculate balance
          const totalDebit = updatedLedger.reduce(
            (sum, entry) => sum + (parseFloat(entry.debit) || 0),
            0
          );
          const totalCredit = updatedLedger.reduce(
            (sum, entry) => sum + (parseFloat(entry.credit) || 0),
            0
          );
          const balance = totalDebit - totalCredit;

          return { ...order, ledger: updatedLedger, balance };
        }
        return order;
      })
    );
  };

  // Add new blank ledger entry
  const addLedgerEntry = (orderId) => {
    setOrders((prevOrders) =>
      prevOrders.map((order) => {
        if (order.id === orderId) {
          const newLedger = [
            ...(order.ledger || []),
            { season: "", productName: "", debit: "", credit: "" }, // new empty row
          ];

          // 🔹 Recalculate balance
          const totalDebit = newLedger.reduce(
            (sum, entry) => sum + (parseFloat(entry.debit) || 0),
            0
          );
          const totalCredit = newLedger.reduce(
            (sum, entry) => sum + (parseFloat(entry.credit) || 0),
            0
          );
          const balance = totalDebit - totalCredit;

          return { ...order, ledger: newLedger, balance };
        }
        return order;
      })
    );
  };


  const removeLedgerEntry = (orderId, index) => {
    setOrders((prevOrders) =>
      prevOrders.map((order) => {
        if (order.id === orderId) {
          const updatedLedger = [...(order.ledger || [])];
          updatedLedger.splice(index, 1); // remove entry at index
          return { ...order, ledger: updatedLedger };
        }
        return order;
      })
    );
  };

  // Save ledger to Firestore
  // Save ledger entries for an order
  const saveLedger = async (orderId) => {
    try {
      const orderToSave = orders.find((order) => order.id === orderId);
      if (!orderToSave) return;

      await updateDoc(doc(db, "orders", orderId), {
        ledger: orderToSave.ledger || [], // make sure it's always an array
      });

      toast.success("Ledger entries saved successfully!");
    } catch (error) {
      console.error("Error saving ledger:", error);
      toast.error("Failed to save ledger entries.");
    }
  };



  const handleReject = (orderId) => {
    setSelectedRejectOrderId(orderId);
    setShowRejectModal(true);
  };

  // ✨ New confirm function
  const confirmRejectOrderWithMessage = async () => {
    if (!rejectionMessage.trim()) {
      toast.error("Please Enter a Rejection Reason.");
      return;
    }

    setIsRejecting(true);

    try {
      const orderRef = doc(db, 'orders', selectedRejectOrderId);
      await updateDoc(orderRef, {
        status: 'Rejected By Logistic',
        rejectionMessage: rejectionMessage.trim(),
        finalApprovedBy: '',
      });

      toast.success("Order Rejected with Message");

      // Reset modal state
      setShowRejectModal(false);
      setRejectionMessage('');
      setSelectedRejectOrderId(null);
      await fetchOrders();
    } catch (err) {
      console.error(err);
      toast.error("Failed to Reject Order");
    } finally {
      setIsRejecting(false);
    }
  };



  const handleRevert = async (orderId) => {
    try {
      const orderRef = doc(db, 'orders', orderId);
      await updateDoc(orderRef, {
        status: 'Pending',
        finalApprovedBy: '', // 👈 clear approval
      });
      toast.warn("Order Reverted To BM/RSM Successfully");
      await fetchOrders();
    } catch (err) {
      console.error(err);
      toast.error("Failed To Revert Order");
    }
  };



  const formatDate = (timestamp) => {
    if (!timestamp?.toDate) return '-';
    return format(timestamp.toDate(), 'HH:mm dd-MM-yyyy');
  };

  const totalPages = Math.ceil(filteredOrders.length / ORDERS_PER_PAGE);
  const paginatedOrders = filteredOrders.slice((currentPage - 1) * ORDERS_PER_PAGE, currentPage * ORDERS_PER_PAGE);

  return (
    <div className={styles.container}>
      <div className={styles.main}>
        <div className={styles.topbar}>
          <div className={styles.logoContainer}>
            <img
              src={logo || "/logo.png"} // use imported logo if available, fallback to public path
              alt="Logo"
              className={styles.logo}
            />
            <div>
              <h2>Logistic Manager Dashboard</h2>
              {userName && <p className={styles.welcome}>Welcome {userName}</p>}
            </div>
          </div>
          <button className={styles.logoutButton} onClick={async () => {
            await auth.signOut();
            navigate("/");
          }}><FaSignOutAlt /> Logout</button>
        </div>

        <div className={styles.content}>
          {mainView === "home" ? (
            <>
              <h2>Orders</h2>
              <div className={styles.filterControls} style={{ marginBottom: 20 }}>
                <select value={rsmFilter} onChange={(e) => setRsmFilter(e.target.value)}>
                  <option value="All">All BM/RSMs</option>
                  {[...new Map(
                    orders.map((o) => [normalizeRsmKey(o.rsmName), normalizeRsmName(o.rsmName)])
                  ).values()].map((rsm, idx) => (
                    <option key={idx} value={rsm}>{rsm}</option>
                  ))}
                </select>

                <select value={soFilter} onChange={(e) => setSoFilter(e.target.value)}>
                  <option value="All">All T.Ms</option>
                  {[...new Set(orders.map((o) => o.soName))].map((so, idx) => (
                    <option key={idx} value={so}>{so}</option>
                  ))}
                </select>

                <select value={partyFilter} onChange={(e) => setPartyFilter(e.target.value)}>
                  <option value="All">All Parties</option>
                  {[...new Set(orders.map((o) => o.partyName))].map((party, idx) => (
                    <option key={idx} value={party}>{party}</option>
                  ))}
                </select>

                <select value={productFilter} onChange={(e) => setProductFilter(e.target.value)}>
                  <option value="All">All Products</option>
                  {[...new Set(
                    orders.flatMap((o) =>
                      (o.products || [])
                        .map((p) => (p.name || "").trim())
                        .filter((name) => name && name.toLowerCase() !== "n/a")
                    )
                  )].sort().map((product, idx) => (
                    <option key={idx} value={product}>{product}</option>
                  ))}
                </select>
              </div>

              {homeRsmGroups.length === 0 ? (
                <p>No orders found.</p>
              ) : (
                <div className={styles.rsmCardGrid}>
                  {homeRsmGroups.map((group) => (
                    <div
                      key={group.rsmName}
                      className={styles.rsmCard}
                      onClick={() => {
                        setSelectedRsm(group.rsmName);
                        setRsmFilter(group.rsmName);
                        setMainView("rsmHome");
                      }}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          setSelectedRsm(group.rsmName);
                          setRsmFilter(group.rsmName);
                          setMainView("rsmHome");
                        }
                      }}
                    >
                      <h3 className={styles.rsmCardName}>{group.rsmName}</h3>
                      <p className={styles.rsmCardCount}>
                        {group.totalCount} Order{group.totalCount !== 1 ? "s" : ""}
                      </p>
                      <span className={styles.rsmCardPending}>{group.pendingCount} Pending</span>
                      {" "}
                      <span className={styles.rsmCardDispatched}>{group.dispatchedCount} Dispatched</span>
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : mainView === "rsmHome" ? (
            <>
              <button
                className={styles.backButton}
                style={{ marginBottom: 16 }}
                onClick={() => {
                  setMainView("home");
                  setSelectedRsm(null);
                  setRsmFilter("All");
                }}
              >
                ← Back to All RSMs
              </button>

              <h2>{selectedRsm}'s Orders</h2>

              <div className={styles.filterControls} style={{ marginBottom: 20 }}>
                <select value={soFilter} onChange={(e) => setSoFilter(e.target.value)}>
                  <option value="All">All T.Ms</option>
                  {[...new Set(orders.map((o) => o.soName))].map((so, idx) => (
                    <option key={idx} value={so}>{so}</option>
                  ))}
                </select>

                <select value={partyFilter} onChange={(e) => setPartyFilter(e.target.value)}>
                  <option value="All">All Parties</option>
                  {[...new Set(orders.map((o) => o.partyName))].map((party, idx) => (
                    <option key={idx} value={party}>{party}</option>
                  ))}
                </select>

                <select value={productFilter} onChange={(e) => setProductFilter(e.target.value)}>
                  <option value="All">All Products</option>
                  {[...new Set(
                    orders.flatMap((o) =>
                      (o.products || [])
                        .map((p) => (p.name || "").trim())
                        .filter((name) => name && name.toLowerCase() !== "n/a")
                    )
                  )].sort().map((product, idx) => (
                    <option key={idx} value={product}>{product}</option>
                  ))}
                </select>
              </div>

              <div className={styles.mainSectionGrid}>
                <div
                  className={styles.mainSectionCard}
                  onClick={() => { setActiveStatus("Pending"); setMainView("table"); }}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      setActiveStatus("Pending");
                      setMainView("table");
                    }
                  }}
                >
                  <h3>Pending</h3>
                  <p className={styles.mainSectionCount}>
                    {orders.filter(
                      (order) =>
                        normalizeRsmKey(order.rsmName) === normalizeRsmKey(selectedRsm) &&
                        (order.status === "BM/RSM Submitted" || order.status === "Placed" || order.status === "Pending")
                    ).length} Orders
                  </p>
                </div>

                <div
                  className={styles.mainSectionCard}
                  onClick={() => { setActiveStatus("Dispatched"); setMainView("table"); }}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      setActiveStatus("Dispatched");
                      setMainView("table");
                    }
                  }}
                >
                  <h3>Dispatched</h3>
                  <p className={styles.mainSectionCount}>
                    {orders.filter(
                      (order) =>
                        normalizeRsmKey(order.rsmName) === normalizeRsmKey(selectedRsm) &&
                        (order.status === "Logistic Reviewed" || order.status === "Approved")
                    ).length} Orders
                  </p>
                </div>

                <div
                  className={styles.mainSectionCard}
                  onClick={() => { setActiveStatus("Rejected"); setMainView("table"); }}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      setActiveStatus("Rejected");
                      setMainView("table");
                    }
                  }}
                >
                  <h3>Rejected</h3>
                  <p className={styles.mainSectionCount}>
                    {orders.filter(
                      (order) =>
                        normalizeRsmKey(order.rsmName) === normalizeRsmKey(selectedRsm) &&
                        (order.status === "Rejected" || order.status === "Rejected By Logistic" || order.status === "Rejected By BM/RSM")
                    ).length} Orders
                  </p>
                </div>
              </div>
            </>
          ) : (
            <>
              <button
                className={styles.backButton}
                style={{ marginBottom: 16 }}
                onClick={() => setMainView("rsmHome")}
              >
                ← Back to {selectedRsm}'s Overview
              </button>

              <h2>Orders</h2>
          <div className={styles.controls}>
            <div className={styles.filters}>
              <button className={styles.refreshBtn} onClick={fetchOrders} disabled={isLoading}>
                🔄 {isLoading ? "Refreshing..." : "Refresh Orders"}
              </button>

              <div className={styles.columnPicker}>
                <button
                  className={styles.refreshBtn}
                  onClick={() => setShowColumnPicker((open) => !open)}
                >
                  🧩 Columns ({TOGGLEABLE_COLUMNS.filter((c) => isColVisible(c.key)).length}/{TOGGLEABLE_COLUMNS.length})
                </button>

                {showColumnPicker && (
                  <div className={styles.columnPickerPanel}>
                    {TOGGLEABLE_COLUMNS.map((col) => (
                      <label key={col.key} className={styles.columnPickerItem}>
                        <input
                          type="checkbox"
                          checked={isColVisible(col.key)}
                          onChange={() => toggleColumn(col.key)}
                        />
                        {col.label}
                      </label>
                    ))}
                    <div className={styles.columnPickerActions}>
                      <button onClick={showAllColumns}>Show all</button>
                      <button onClick={() => setShowColumnPicker(false)}>Done</button>
                    </div>
                  </div>
                )}
              </div>
            </div>


            <div className={styles.filterControls}>
              <select value={soFilter} onChange={(e) => setSoFilter(e.target.value)}>
                <option value="All">All T.Ms</option>
                {[...new Set(orders.map((o) => o.soName))].map((so, idx) => (
                  <option key={idx} value={so}>{so}</option>
                ))}
              </select>

              <select value={partyFilter} onChange={(e) => setPartyFilter(e.target.value)}>
                <option value="All">All Parties</option>
                {[...new Set(orders.map((o) => o.partyName))].map((party, idx) => (
                  <option key={idx} value={party}>{party}</option>
                ))}
              </select>

              <select value={productFilter} onChange={(e) => setProductFilter(e.target.value)}>
                <option value="All">All Products</option>
                {[...new Set(
                  orders.flatMap((o) =>
                    (o.products || [])
                      .map((p) => (p.name || "").trim())
                      .filter((name) => name && name.toLowerCase() !== "n/a")
                  )
                )].sort().map((product, idx) => (
                  <option key={idx} value={product}>{product}</option>
                ))}
              </select>
              <div className={styles.dateFilter}>
                <label>From</label>
                <input
                  type="date"
                  value={dateRange.from}
                  onChange={(e) => setDateRange({ ...dateRange, from: e.target.value })}
                />
                <label>To</label>
                <input
                  type="date"
                  value={dateRange.to}
                  onChange={(e) => setDateRange({ ...dateRange, to: e.target.value })}
                />

                <label>Commitment Date</label>
                <input
                  type="date"
                  value={commitmentDateFilter}
                  onChange={(e) => setCommitmentDateFilter(e.target.value)}
                />
              </div>
            </div>
            <button
              onClick={handleExportSelected}
              disabled={selectedOrderIds.length === 0}
              className={styles.exportButton}
            >
              📤 Export
            </button>
            <button
              onClick={handleExportPDF}
              disabled={selectedOrderIds.length === 0}
              className={styles.exportButton}
            >
              📝 Export PDF
            </button>
          </div>


          {showRejectModal && (
            <div className={styles.modalOverlay}>
              <div className={styles.modalBox}>
                <h2>Reject Order</h2>
                <textarea
                  className={styles.modalTextarea}
                  placeholder="Enter Rejection Reason..."
                  value={rejectionMessage}
                  onChange={(e) => setRejectionMessage(e.target.value)}
                />
                <div className={styles.modalActions}>
                  <button
                    className={styles.modalCancelBtn}
                    onClick={() => {
                      setShowRejectModal(false);
                      setRejectionMessage('');
                      setSelectedRejectOrderId(null);
                    }}
                    disabled={isRejecting}
                  >
                    Cancel
                  </button>
                  <button
                    className={styles.modalSubmitBtn}
                    onClick={confirmRejectOrderWithMessage}
                    disabled={isRejecting}
                  >
                    {isRejecting ? "Rejecting..." : "Confirm Reject"}
                  </button>
                </div>
              </div>
            </div>
          )}


          {viewRejectModalOpen && (
            <div className={styles.modalOverlay}>
              <div className={styles.modalBox}>
                <h2>Rejection Message</h2>
                <p>{viewRejectMessage}</p>
                <div className={styles.modalActions}>
                  <button
                    className={styles.modalCancelBtn}
                    onClick={() => {
                      setViewRejectModalOpen(false);
                      setViewRejectMessage('');
                    }}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          )}


          {isLoading ? (
            <p>Loading orders...</p>
          ) : filteredOrders.length === 0 ? (
            <p>No {activeStatus.toLowerCase()} orders found for {selectedRsm}.</p>
          ) : (
            <>
              <div className={styles.tableWrapper}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th><input
                        type="checkbox"
                        checked={
                          paginatedOrders.length > 0 &&
                          paginatedOrders.every((order) => selectedOrderIds.includes(order.id))
                        }
                        onChange={(e) => {
                          const isChecked = e.target.checked;
                          const pageOrderIds = paginatedOrders.map((order) => order.id);
                          setSelectedOrderIds((prevSelected) => {
                            if (isChecked) {
                              // Add all paginated orders (if not already included)
                              const newSet = new Set([...prevSelected, ...pageOrderIds]);
                              return Array.from(newSet);
                            } else {
                              // Remove all paginated orders
                              return prevSelected.filter((id) => !pageOrderIds.includes(id));
                            }
                          });
                        }}
                      />Select</th>
                      <th hidden={!isColVisible("date")}>Date</th>
                      <th hidden={!isColVisible("placedBy")}>Order Placed By</th>
                      <th hidden={!isColVisible("rsm")}>BM/RSM</th>
                      <th hidden={!isColVisible("partyCode")}>Party Code</th>
                      <th hidden={!isColVisible("partyName")}>Party Name</th>
                      <th hidden={!isColVisible("partyNumber")}>Party Number</th>
                      <th hidden={!isColVisible("pod")}>POD</th>
                      <th hidden={!isColVisible("contactInfo")}>Contact Info</th>
                      <th hidden={!isColVisible("products")}>Products</th>
                      {/* <th>Balance</th> */}
                      <th hidden={!isColVisible("commitment")}>Commitment</th>
                      <th hidden={!isColVisible("ledger")}>Ledger</th>
                      <th hidden={!isColVisible("status")}>Status</th>
                      <th hidden={!isColVisible("finalApproval")}>Final Approval By</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedOrders.map(order => (
                      <tr key={order.id} className={
                        order.status === "Approved"
                          ? styles.bossApproved
                          : (order.status === "Rejected" || order.status === "Rejected By Logistic")
                            ? styles.bossRejected
                            : ""
                      }>
                        <td>
                          <input
                            type="checkbox"
                            checked={selectedOrderIds.includes(order.id)}
                            onChange={(e) => {
                              const isChecked = e.target.checked;
                              setSelectedOrderIds((prev) =>
                                isChecked ? [...prev, order.id] : prev.filter((id) => id !== order.id)
                              );
                            }}
                          />
                        </td>
                        <td hidden={!isColVisible("date")}>{formatDate(order.createdAt)}</td>
                        <td hidden={!isColVisible("placedBy")}>{order.soName || 'N/A'}</td>
                        <td hidden={!isColVisible("rsm")}>{order.rsmName || 'N/A'}</td>
                        <td hidden={!isColVisible("partyCode")}>{order.partyCode || 'N/A'}</td>
                        <td hidden={!isColVisible("partyName")}>{order.partyName}</td>
                        <td hidden={!isColVisible("partyNumber")}>{order.partyMobile || order.phone || 'N/A'}</td>
                        <td hidden={!isColVisible("pod")}>{order.pod || 'N/A'}</td>
                        <td hidden={!isColVisible("contactInfo")}>{order.contactInfo || 'N/A'}</td>
                        <td hidden={!isColVisible("products")}>
                          <table className={styles.innerTable}>
                            <thead>
                              <tr>
                                {/* <th>Season</th>  */}
                                <th>Category</th>
                                <th>Product</th>
                                <th>Variety</th>
                                <th>Pack Size</th>
                                <th>Pack Type</th>
                                <th>Qty</th>
                                {/* <th>Credit</th>
                                <th>Debit</th> */}
                              </tr>
                            </thead>
                            <tbody>
                              {order.products?.map((product, i) => (
                                <tr key={i}>
                                  {/* <td>
                                    {["BM/RSM Submitted", "Placed"].includes(order.status) ? (
                                      <input
                                        type="text"
                                        value={product.season || ''}
                                        onChange={(e) =>
                                          handleProductChange(order.id, i, 'season', e.target.value)
                                        }
                                      />
                                    ) : product.season || 'N/A'}
                                  </td> */}
                                  <td>
                                    {["BM/RSM Submitted", "Placed"].includes(order.status) ? (
                                      <input
                                        type="text"
                                        value={product.category || ''}
                                        onChange={(e) =>
                                          handleProductChange(order.id, i, 'category', e.target.value)
                                        }
                                      />
                                    ) : product.category || 'N/A'}
                                  </td>
                                  <td>
                                    {["BM/RSM Submitted", "Placed"].includes(order.status) ? (
                                      <input
                                        type="text"
                                        value={product.name || ''}
                                        onChange={(e) =>
                                          handleProductChange(order.id, i, 'name', e.target.value)
                                        }
                                      />
                                    ) : product.name || 'N/A'}
                                  </td>
                                  <td>
                                    {["BM/RSM Submitted", "Placed"].includes(order.status) ? (
                                      <input
                                        type="text"
                                        value={product.variety || ''}
                                        onChange={(e) =>
                                          handleProductChange(order.id, i, 'variety', e.target.value)
                                        }
                                      />
                                    ) : product.variety || 'N/A'}
                                  </td>
                                  <td>
                                    {["BM/RSM Submitted", "Placed"].includes(order.status) ? (
                                      <input
                                        type="text"
                                        value={product.packSize || ''}
                                        onChange={(e) =>
                                          handleProductChange(order.id, i, 'packSize', e.target.value)
                                        }
                                      />
                                    ) : product.packSize || 'N/A'}
                                  </td>
                                  <td>
                                    {["BM/RSM Submitted", "Placed"].includes(order.status) ? (
                                      <input
                                        type="text"
                                        value={product.packType || ''}
                                        onChange={(e) =>
                                          handleProductChange(order.id, i, 'packType', e.target.value)
                                        }
                                      />
                                    ) : product.packType || 'N/A'}
                                  </td>
                                  <td>
                                    {["BM/RSM Submitted", "Placed"].includes(order.status) ? (
                                      <input
                                        type="number"
                                        value={product.quantity || 0}
                                        onChange={(e) =>
                                          handleProductChange(order.id, i, 'quantity', e.target.value)
                                        }
                                      />
                                    ) : product.quantity || 0}
                                  </td>
                                  {/* <td>
                                    {["BM/RSM Submitted", "Placed"].includes(order.status) ? (
                                      <input
                                        type="number"
                                        value={product.credit || ''}
                                        onChange={(e) =>
                                          handleProductChange(order.id, i, 'credit', e.target.value)
                                        }
                                      />
                                    ) : product.credit || '0'}
                                  </td>
                                  <td>
                                    {["BM/RSM Submitted", "Placed"].includes(order.status) ? (
                                      <input
                                        type="number"
                                        value={product.debit || ''}
                                        onChange={(e) =>
                                          handleProductChange(order.id, i, 'debit', e.target.value)
                                        }
                                      />
                                    ) : product.debit || '0'}
                                  </td> */}
                                  <td>
                                    {["BM/RSM Submitted", "Placed"].includes(order.status) && (
                                      <button
                                        onClick={() => handleRemoveProduct(order.id, i)}
                                        className={styles.removeProductBtn}
                                      >
                                        ❌
                                      </button>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                            <tfoot>
                              <tr>
                                <td colSpan="5"><b>Total Quantity</b></td>
                                <td colSpan={["BM/RSM Submitted", "Placed"].includes(order.status) ? "2" : "1"}>
                                  {(() => {
                                    const totalQty = (order.products || []).reduce(
                                      (acc, product) => acc + (parseFloat(product.quantity) || 0),
                                      0
                                    );
                                    return totalQty;
                                  })()}
                                </td>
                              </tr>
                            </tfoot>
                          </table>

                          {["BM/RSM Submitted", "Placed"].includes(order.status) && (
                            <button
                              className={styles.addProductBtn}
                              onClick={() => handleAddProduct(order.id)}
                            >
                              ➕ Add Product
                            </button>
                          )}
                        </td>
                        {/* <td>
                          {order.balance > 0
                            ? `Rs. ${order.balance} (Credit)`
                            : order.balance < 0
                              ? `Rs. ${Math.abs(order.balance)} (Debit)`
                              : "Rs. 0"}
                        </td> */}
                        <td hidden={!isColVisible("commitment")}>
                          {(order.status === "BM/RSM Submitted" || order.status === "Placed") ? (
                            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                              <input
                                type="text"
                                placeholder="Commitment note"
                                value={order.commitmentOfPayment || order.commitmentMessage || ""}
                                onChange={(e) =>
                                  handleCommitmentChange(order.id, "commitmentOfPayment", e.target.value)
                                }
                              />
                              <input
                                type="date"
                                value={
                                  order.commitmentDate
                                    ? (order.commitmentDate.toDate
                                      ? format(order.commitmentDate.toDate(), 'yyyy-MM-dd')
                                      : (typeof order.commitmentDate === "string"
                                        ? order.commitmentDate.slice(0, 10)
                                        : format(new Date(order.commitmentDate), 'yyyy-MM-dd')))
                                    : ""
                                }
                                onChange={(e) =>
                                  handleCommitmentChange(order.id, "commitmentDate", e.target.value)
                                }
                              />
                              <button onClick={() => saveCommitment(order.id)}>Save</button>
                            </div>
                          ) : (
                            <>
                              <em>{order.commitmentOfPayment || order.commitmentMessage || ''}</em>
                              <br />
                              {order.commitmentDate
                                ? (order.commitmentDate.toDate
                                  ? format(order.commitmentDate.toDate(), 'dd-MM-yyyy')
                                  : format(new Date(order.commitmentDate), 'dd-MM-yyyy')
                                )
                                : 'N/A'}
                            </>
                          )}
                        </td>
                        <td hidden={!isColVisible("ledger")}>
                          {((order.ledger && order.ledger.length > 0) ||
                            order.status === "BM/RSM Submitted" ||
                            order.status === "Placed") && (
                            <div className="ledger-section">
                              {(!order.ledger || order.ledger.length === 0) && (
                                <p style={{ fontSize: "0.85rem", fontStyle: "italic", margin: "0 0 6px 0" }}>
                                  No ledger entries yet.
                                </p>
                              )}
                              <table className="ledger-table">
                                <thead>
                                  <tr>
                                    <th>Season</th>
                                    <th>Product</th>
                                    <th>Debit</th>
                                    <th>Credit</th>
                                    {(order.status === "BM/RSM Submitted" || order.status === "Placed") && (
                                      <th>Action</th>
                                    )}
                                  </tr>
                                </thead>
                                <tbody>
                                  {(order.ledger || []).map((entry, idx) => (
                                    <tr key={idx}>
                                      <td>
                                        {(order.status === "BM/RSM Submitted" || order.status === "Placed") ? (
                                          <input
                                            type="text"
                                            value={entry.season}
                                            onChange={(e) =>
                                              handleLedgerChange(order.id, idx, "season", e.target.value)
                                            }
                                          />
                                        ) : (
                                          entry.season
                                        )}
                                      </td>
                                      <td>
                                        {(order.status === "BM/RSM Submitted" || order.status === "Placed") ? (
                                          <select
                                            value={entry.productName || ""}
                                            onChange={(e) =>
                                              handleLedgerChange(order.id, idx, "productName", e.target.value)
                                            }
                                          >
                                            <option value="">Select Product</option>
                                            {/* keep an already-saved value visible even if it isn't in the catalog */}
                                            {entry.productName && !catalogProductNames.includes(entry.productName) && (
                                              <option value={entry.productName}>{entry.productName}</option>
                                            )}
                                            {catalogProductNames.map((name) => (
                                              <option key={name} value={name}>{name}</option>
                                            ))}
                                          </select>
                                        ) : (
                                          entry.productName
                                        )}
                                      </td>
                                      <td>
                                        {(order.status === "BM/RSM Submitted" || order.status === "Placed") ? (
                                          <input
                                            type="number"
                                            value={entry.debit}
                                            onChange={(e) =>
                                              handleLedgerChange(order.id, idx, "debit", e.target.value)
                                            }
                                          />
                                        ) : (
                                          entry.debit ? `${entry.debit} Dr` : "0"
                                        )}
                                      </td>
                                      <td>
                                        {(order.status === "BM/RSM Submitted" || order.status === "Placed") ? (
                                          <input
                                            type="number"
                                            value={entry.credit}
                                            onChange={(e) =>
                                              handleLedgerChange(order.id, idx, "credit", e.target.value)
                                            }
                                          />
                                        ) : (
                                          entry.credit ? `${entry.credit} Cr` : "0"
                                        )}
                                      </td>
                                      {(order.status === "BM/RSM Submitted" || order.status === "Placed") && (
                                        <td>
                                          <button
                                            onClick={() => removeLedgerEntry(order.id, idx)}
                                            className="remove-btn"
                                          >
                                            Remove
                                          </button>
                                        </td>
                                      )}
                                    </tr>
                                  ))}
                                </tbody>
                                <tfoot>
                                  <tr>
                                    <td colSpan="2"><b>Balance</b></td>
                                    <td colSpan="2">
                                      {(() => {
                                        const balance = (order.ledger || []).reduce(
                                          (acc, entry) => acc + (parseFloat(entry.credit) || 0) - (parseFloat(entry.debit) || 0),
                                          0
                                        );
                                        return balance >= 0 ? `${balance} Cr` : `${Math.abs(balance)} Dr`;
                                      })()}
                                    </td>
                                  </tr>
                                </tfoot>
                              </table>

                              {/* Add new entry option */}
                              {(order.status === "BM/RSM Submitted" || order.status === "Placed") && (
                                <>
                                  <button
                                    onClick={() => addLedgerEntry(order.id)}
                                    className="add-btn"
                                  >
                                    + Add Entry
                                  </button>
                                  <button
                                    onClick={() => saveLedger(order.id)}
                                    className="save-btn"
                                  >
                                    Save Ledger
                                  </button>
                                </>
                              )}
                            </div>
                          )}
                        </td>



                        <td hidden={!isColVisible("status")}>
                          {order.status}
                          {(order.status === "Rejected" || order.status === "Rejected By Logistic") && order.rejectionMessage && (
                            <span
                              title="View Rejection Message"
                              onClick={() => {
                                setViewRejectMessage(order.rejectionMessage);
                                setViewRejectModalOpen(true);
                              }}
                              style={{ marginLeft: 8, cursor: "pointer", color: "#e74c3c", fontWeight: "bold" }}
                            >
                              ❗
                            </span>
                          )}
                        </td>

                        <td hidden={!isColVisible("finalApproval")}>{order.finalApprovedByName || 'N/A'}</td>
                        <td>
                          {order.status === "BM/RSM Submitted" && (
                            <>
                              <button className={styles.approveBtn} onClick={() => handleApprove(order)}>Approve</button>
                              <button className={styles.rejectButton} onClick={() => handleReject(order.id)}>Reject</button>
                              <button className={styles.revertButton} onClick={() => handleRevert(order.id)}>Revert</button>
                            </>
                          )}
                          {order.status === "Placed" && (
                            <>
                              <button className={styles.approveBtn} onClick={() => handleApprove(order)}>Approve</button>
                              <button className={styles.rejectButton} onClick={() => handleReject(order.id)}>Reject</button>
                            </>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className={styles.pagination}>
                <button onClick={() => setCurrentPage(p => Math.max(p - 1, 1))} disabled={currentPage === 1}>Prev</button>
                <span>Page {currentPage} of {totalPages}</span>
                <button onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))} disabled={currentPage === totalPages}>Next</button>
              </div>
            </>
          )}
            </>
          )}
        </div>
      </div>
      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
};

export default LogisticManagerDashboard;
